import React, { useState, useEffect, useReducer, useContext } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import SubscriptionModal from "../../components/SubscriptionModal";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import moment from "moment";

const reducer = (state, action) => {
  if (action.type === "LOAD_INVOICES") {
    const invoices = action.payload.invoices || action.payload;
    const newInvoices = [];
    invoices.forEach((invoice) => {
      const idx = state.findIndex((i) => i.id === invoice.id);
      if (idx !== -1) state[idx] = invoice;
      else newInvoices.push(invoice);
    });
    return [...state, ...newInvoices];
  }
  if (action.type === "UPDATE_INVOICES") {
    const invoice = action.payload;
    const idx = state.findIndex((i) => i.id === invoice.id);
    if (idx !== -1) { state[idx] = invoice; return [...state]; }
    return [invoice, ...state];
  }
  if (action.type === "DELETE_INVOICE") {
    const id = action.payload;
    const idx = state.findIndex((i) => i.id === id);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  tableHead: {
    "& th": {
      fontWeight: 700,
      fontSize: 11,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      padding: "8px 12px",
    },
  },
  expiredBanner: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    padding: "10px 14px",
    borderRadius: 4,
    marginTop: 10,
    border: "1px solid #ef9a9a",
    fontSize: 14,
  },
  /* Mobile card list */
  cardList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "8px 4px",
  },
  card: {
    border: "1px solid rgba(0,0,0,0.1)",
    borderRadius: 10,
    padding: "12px 14px",
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: 15,
    color: "#1e293b",
  },
  cardRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 13,
    color: "#475569",
  },
  cardLabel: {
    fontWeight: 600,
    color: "#94a3b8",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  cardValue: {
    fontWeight: 500,
    color: "#334155",
    fontSize: 13,
  },
  cardFooter: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 4,
  },
}));

const getStatusColor = (status) => {
  if (status === i18n.t("invoices.status.paid")) return { bg: "rgba(34,197,94,0.12)", color: "#16a34a" };
  if (status === i18n.t("invoices.status.overdue")) return { bg: "rgba(239,68,68,0.12)", color: "#dc2626" };
  return { bg: "rgba(234,179,8,0.12)", color: "#92400e" };
};

const Invoices = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam] = useState("");
  const [invoices, dispatch] = useReducer(reducer, []);
  const [storagePlans, setStoragePlans] = React.useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [isCompanyExpired, setIsCompanyExpired] = useState(false);

  useEffect(() => {
    if (user && user.company) {
      setIsCompanyExpired(moment().isAfter(moment(user.company.dueDate)));
    }
  }, [user]);

  const handleOpenContactModal = (invoice) => {
    setStoragePlans(invoice);
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchInvoices = async () => {
        try {
          const { data } = await api.get("/invoices/all", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_INVOICES", payload: data });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchInvoices();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  const loadMore = () => setPageNumber((prev) => prev + 1);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  const rowStyle = (record) => {
    const hoje = moment().format("DD/MM/yyyy");
    const venc = moment(record.dueDate).format("DD/MM/yyyy");
    const dias = moment.duration(moment(venc, "DD/MM/yyyy").diff(moment(hoje, "DD/MM/yyyy"))).asDays();
    if (dias < 0 && record.status !== "paid") return { backgroundColor: "#ffbcbc9c" };
  };

  const rowStatus = (record) => {
    const hoje = moment().format("DD/MM/yyyy");
    const venc = moment(record.dueDate).format("DD/MM/yyyy");
    const dias = moment.duration(moment(venc, "DD/MM/yyyy").diff(moment(hoje, "DD/MM/yyyy"))).asDays();
    if (record.status === "paid") return i18n.t("invoices.status.paid");
    if (dias < 0) return i18n.t("invoices.status.overdue");
    return i18n.t("invoices.status.open");
  };

  const formatValue = (val) =>
    val != null
      ? val.toLocaleString("es-MX", { style: "currency", currency: "MXN" })
      : "MX$0.00";

  const isPaid = (record) => record.status === "paid";

  return (
    <MainContainer>
      <SubscriptionModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        Invoice={storagePlans}
        contactId={selectedContactId}
      />
      <MainHeader>
        <Title>{i18n.t("invoices.title")} ({invoices.length})</Title>
        {isCompanyExpired && (
          <div className={classes.expiredBanner}>
            <strong>{i18n.t("invoices.expiredAlert.attention")}:</strong>{" "}
            {i18n.t("invoices.expiredAlert.message")}
          </div>
        )}
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        {isMobile ? (
          /* ── MOBILE: card list ── */
          <div className={classes.cardList}>
            {invoices.map((invoice) => {
              const status = rowStatus(invoice);
              const { bg, color } = getStatusColor(status);
              const overdue = status === i18n.t("invoices.status.overdue");
              return (
                <div
                  key={invoice.id}
                  className={classes.card}
                  style={{ borderLeft: `4px solid ${overdue ? "#dc2626" : color}` }}
                >
                  <div className={classes.cardHeader}>
                    <span className={classes.cardTitle}>{invoice.detail || "—"}</span>
                    <Chip
                      label={status}
                      size="small"
                      style={{ backgroundColor: bg, color, fontWeight: 700, fontSize: 11, height: 22 }}
                    />
                  </div>

                  <div className={classes.cardRow}>
                    <div>
                      <div className={classes.cardLabel}>{i18n.t("invoices.table.users")}</div>
                      <div className={classes.cardValue}>{invoice.users}</div>
                    </div>
                    <div>
                      <div className={classes.cardLabel}>{i18n.t("invoices.table.connections")}</div>
                      <div className={classes.cardValue}>{invoice.connections}</div>
                    </div>
                    <div>
                      <div className={classes.cardLabel}>{i18n.t("invoices.table.queues")}</div>
                      <div className={classes.cardValue}>{invoice.queues}</div>
                    </div>
                  </div>

                  <div className={classes.cardRow}>
                    <div>
                      <div className={classes.cardLabel}>{i18n.t("invoices.table.value")}</div>
                      <div className={classes.cardValue} style={{ fontWeight: 700 }}>{formatValue(invoice.value)}</div>
                    </div>
                    <div>
                      <div className={classes.cardLabel}>{i18n.t("invoices.table.dueDate")}</div>
                      <div className={classes.cardValue}>{moment(invoice.dueDate).format("DD/MM/YYYY")}</div>
                    </div>
                  </div>

                  <div className={classes.cardFooter}>
                    {!isPaid(invoice) ? (
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        onClick={() => handleOpenContactModal(invoice)}
                      >
                        {i18n.t("invoices.buttons.pay")}
                      </Button>
                    ) : (
                      <Button size="small" variant="outlined" disabled>
                        {i18n.t("invoices.buttons.paid")}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            {loading && <div style={{ textAlign: "center", padding: 16, color: "#94a3b8" }}>...</div>}
          </div>
        ) : (
          /* ── DESKTOP: table ── */
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell align="center">{i18n.t("invoices.table.details")}</TableCell>
                <TableCell align="center">{i18n.t("invoices.table.users")}</TableCell>
                <TableCell align="center">{i18n.t("invoices.table.connections")}</TableCell>
                <TableCell align="center">{i18n.t("invoices.table.queues")}</TableCell>
                <TableCell align="center">{i18n.t("invoices.table.value")}</TableCell>
                <TableCell align="center">{i18n.t("invoices.table.dueDate")}</TableCell>
                <TableCell align="center">{i18n.t("invoices.table.status")}</TableCell>
                <TableCell align="center">{i18n.t("invoices.table.action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <>
                {invoices.map((invoice) => {
                  const status = rowStatus(invoice);
                  const { bg, color } = getStatusColor(status);
                  return (
                    <TableRow style={rowStyle(invoice)} key={invoice.id}>
                      <TableCell align="center">{invoice.detail}</TableCell>
                      <TableCell align="center">{invoice.users}</TableCell>
                      <TableCell align="center">{invoice.connections}</TableCell>
                      <TableCell align="center">{invoice.queues}</TableCell>
                      <TableCell style={{ fontWeight: "bold" }} align="center">
                        {formatValue(invoice.value)}
                      </TableCell>
                      <TableCell align="center">{moment(invoice.dueDate).format("DD/MM/YYYY")}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={status}
                          size="small"
                          style={{ backgroundColor: bg, color, fontWeight: 700, fontSize: 11, height: 22 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {!isPaid(invoice) ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={() => handleOpenContactModal(invoice)}
                          >
                            {i18n.t("invoices.buttons.pay")}
                          </Button>
                        ) : (
                          <Button size="small" variant="outlined" disabled>
                            {i18n.t("invoices.buttons.paid")}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {loading && <TableRowSkeleton columns={8} />}
              </>
            </TableBody>
          </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Invoices;
